import { createOperationDescriptor } from 'relay-runtime';
import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';

import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import ObjectCommentContainerPreloadObjectCommentsQuery from '../components/__generated__/ObjectCommentContainerPreloadObjectCommentsQuery.graphql.ts';
import { getOrCreateConversationQueryOwner } from '../ConversationQueryOwner.ts';
import { PRELOAD_LOADING_FLAG } from '../components/helpers/ObjectCommentContainerHelpers.ts';
import { buildPreloadObjectCommentsQueryVariables } from '../preloadObjectCommentsVariables.ts';
import type {
	ObjectCommentContainerPreloadObjectCommentsQuery$data,
	ObjectCommentContainerPreloadObjectCommentsQuery$variables,
} from '../components/__generated__/ObjectCommentContainerPreloadObjectCommentsQuery.graphql.ts';

/**
 * Standalone function to preload footer comments
 * This can be called outside of React components (e.g., in route preloaders)
 */
export const preloadObjectComments = async (
	contentId: string,
	focusedCommentId?: string,
): Promise<{
	data: ObjectCommentContainerPreloadObjectCommentsQuery$data | null;
}> => {
	if (!contentId) {
		return Promise.resolve({ data: null });
	}

	// Clear the cache BEFORE load
	// Don't clear in SSR - if the preload times out there we won't have a way to overwrite this
	try {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR) {
			writeQueryInterop<
				ObjectCommentContainerPreloadObjectCommentsQuery$data,
				ObjectCommentContainerPreloadObjectCommentsQuery$variables
			>({
				query: ObjectCommentContainerPreloadObjectCommentsQuery,
				variables: buildPreloadObjectCommentsQueryVariables(
					contentId,
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_conversation_data_frag_reader') &&
						expVal('confluence_exp_conversation_contract', 'isEnabled', false),
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_comment_threads_frag_reader') &&
						expVal('confluence_exp_conversation_contract', 'isEnabled', false),
					focusedCommentId,
					Boolean(focusedCommentId) &&
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_conversation_query') &&
						expVal('confluence_exp_conversation_contract', 'isEnabled', false),
				),
				data: {
					comments: {
						nodes: [],
						totalCount: PRELOAD_LOADING_FLAG,
						__typename: 'PaginatedCommentList',
					},
				},
			});
		}
	} catch {
		// Do nothing, this is expected if the query is not in the cache
	}

	const variables = buildPreloadObjectCommentsQueryVariables(
		contentId,
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_conversation_data_frag_reader') &&
			expVal('confluence_exp_conversation_contract', 'isEnabled', false),
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_comment_threads_frag_reader') &&
			expVal('confluence_exp_conversation_contract', 'isEnabled', false),
		focusedCommentId,
		Boolean(focusedCommentId) &&
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_conversation_query') &&
			expVal('confluence_exp_conversation_contract', 'isEnabled', false),
	);
	if (
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_conversation_preloaded_query') &&
		expVal('confluence_exp_conversation_contract', 'isEnabled', false)
	) {
		const environment = getRelayEnvironment();
		const owner = getOrCreateConversationQueryOwner(environment);
		// Keep the existing route freshness policy. Concurrent consumers share this execution.
		const reference = owner.preload(variables, 'network-only');
		await reference.ready;
		return {
			data:
				reference.disposed || reference.failed
					? null
					: (environment.lookup(
							createOperationDescriptor(
								ObjectCommentContainerPreloadObjectCommentsQuery,
								reference.variables,
							).fragment,
						).data as ObjectCommentContainerPreloadObjectCommentsQuery$data),
		};
	}

	// Use query from preloader-tools to trigger the network-only query
	// This will fetch the data and update the cache automatically
	// The hook query will pick up the changes from cache
	const result = await query<
		ObjectCommentContainerPreloadObjectCommentsQuery$data,
		ObjectCommentContainerPreloadObjectCommentsQuery$variables
	>({
		query: ObjectCommentContainerPreloadObjectCommentsQuery,
		variables,
		fetchPolicy: 'network-only',
	});

	// Return in the same format as queryInterop for compatibility
	return { data: result.data };
};
